import { Card, DatePicker, Form, Input, message, Spin, Switch } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { TextField } from '../../components/form-fields/text-field';
import { TimezoneSelect } from '../../components/timezone-select';
import { BusinessUnitsSelectTree } from '../../components/region-selectors/business-units-select-tree';
import { useHandleFormSelectionChange } from '../../components/region-selectors/business-units-select-tree/hooks/use-handle-form-selection-change';
import { ImageInput } from '../../components/image-input';
import { useAuthStore, useEventStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { EventTranslationRequest } from '../../stores/events';
import type { PromotionFormProvince } from '../../stores/promotion';
import { EventStatusTag, getEventStatus } from '../../utils/event-status';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
    DATE_TIME_FORMAT,
    getUserTimezoneOrFallback,
} from '../../utils/timezone';
import { useNavigation } from '../../utils/use-navigation';
import { usePlacementLinking } from '../../utils/use-placement-linking';
import style from './style.module.scss';

const { TextArea } = Input;

const SUGGESTED_TITLE_LENGTH = 18;
const SUGGESTED_SUBTITLE_LENGTH = 28;
const MAX_LENGTH = 255;

type Province = {
    id: number;
    name: string;
    businessUnit: BusinessUnit;
};

type BusinessUnit = {
    id: number;
    name: string;
};

type EventProvince = {
    id: number;
    province: Province;
};

interface EventDates {
    startDate?: moment.Moment;
    endDate?: moment.Moment;
}

interface EventFormData {
    translations: {
        en: Omit<EventTranslationRequest, 'language'>;
        fr: Omit<EventTranslationRequest, 'language'>;
    };
    timezone: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
    isActive: boolean;
    latitude?: number | string;
    longitude?: number | string;
    eventProvinces: EventProvince[];
    provinces: PromotionFormProvince[];
}

interface TranslationFormProps {
    language: 'en' | 'fr';
    form: ReturnType<typeof Form.useForm>[0];
    setFormChanged: (changed: boolean) => void;
}

const TranslationForm: React.FC<TranslationFormProps> = ({
    language,
    form,
    setFormChanged,
}) => {
    const languageLabel = language === 'en' ? 'English' : 'French';

    const handleImageChange = (url: string | null) => {
        const currentTranslations = form.getFieldValue('translations') || {};
        const currentLanguageData =
            form.getFieldValue(['translations', language]) || {};

        const newTranslations = {
            ...currentTranslations,
            ...(language === 'en'
                ? { en: { ...currentLanguageData, image: url } }
                : { fr: { ...currentLanguageData, image: url } }),
        };

        form.setFieldsValue({ translations: newTranslations });
        setFormChanged(true);
    };

    const handleImageDelete = () => {
        const currentTranslations = form.getFieldValue('translations') || {};
        const currentLanguageData =
            form.getFieldValue(['translations', language]) || {};

        const newTranslations = {
            ...currentTranslations,
            ...(language === 'en'
                ? { en: { ...currentLanguageData, image: null } }
                : { fr: { ...currentLanguageData, image: null } }),
        };

        form.setFieldsValue({ translations: newTranslations });
        setFormChanged(true);
    };

    return (
        <div className={style.language_section}>
            <h2>{languageLabel.toUpperCase()}</h2>

            <div className={style.image_uploaders}>
                <div>
                    <label className={'primary_color'}>Image</label>
                    <Form.Item
                        name={['translations', language, 'image']}
                        rules={[
                            {
                                required: true,
                                message: 'Please select an image',
                            },
                        ]}
                        style={{ marginBottom: 0 }}
                    >
                        <ImageInput
                            url={form.getFieldValue([
                                'translations',
                                language,
                                'image',
                            ])}
                            onChange={handleImageChange}
                            onDelete={handleImageDelete}
                        />
                    </Form.Item>
                </div>
            </div>

            <div>
                <label className={'primary_color'}>Title</label>
                <Form.Item
                    name={['translations', language, 'title']}
                    rules={[
                        { required: true, message: 'Please enter event title' },
                    ]}
                    style={{ marginBottom: 10 }}
                >
                    <TextField
                        placeholder="Enter event title"
                        maxLength={MAX_LENGTH}
                        suggestedLength={SUGGESTED_TITLE_LENGTH}
                    />
                </Form.Item>

                <label className={'primary_color'}>Subtitle</label>
                <Form.Item
                    name={['translations', language, 'subtitle']}
                    style={{ marginBottom: 10 }}
                >
                    <TextField
                        placeholder="Enter event subtitle"
                        maxLength={MAX_LENGTH}
                        suggestedLength={SUGGESTED_SUBTITLE_LENGTH}
                    />
                </Form.Item>

                <label className={'primary_color'}>Description</label>
                <Form.Item
                    name={['translations', language, 'description']}
                    style={{ marginBottom: 10 }}
                >
                    <TextArea placeholder="Enter event description" />
                </Form.Item>

                <label className={'primary_color'}>Button Text</label>
                <Form.Item
                    name={['translations', language, 'ctaText']}
                    rules={[
                        { required: true, message: 'Please enter button text' },
                    ]}
                    style={{ marginBottom: 10 }}
                >
                    <Input placeholder="Enter button text" />
                </Form.Item>

                <label className={'primary_color'}>Map Button Text</label>
                <Form.Item
                    name={['translations', language, 'mapButtonText']}
                    style={{ marginBottom: 10 }}
                >
                    <Input placeholder="Enter map button text" />
                </Form.Item>
            </div>
        </div>
    );
};

function EventConfigSection() {
    const { handleProvinceSelectionChange } = useHandleFormSelectionChange();
    const form = Form.useFormInstance();

    return (
        <div className={style.form_section}>
            <div className={style.config_row}>
                <div>
                    <h5 className={style.subsection_header}>Schedule</h5>
                    <div className={style.date_row}>
                        <Form.Item
                            name="timezone"
                            label="Timezone"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select a timezone',
                                },
                            ]}
                            className={style.date_item}
                        >
                            <TimezoneSelect />
                        </Form.Item>
                    </div>
                    <div className={style.date_row}>
                        <Form.Item
                            name="startDate"
                            label="Start Date & Time"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select start date',
                                },
                            ]}
                            className={style.date_item}
                        >
                            <DatePicker
                                showTime
                                format={DATE_TIME_FORMAT}
                                placeholder="Select start date and time"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="endDate"
                            label="End Date & Time"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select end date',
                                },
                            ]}
                            className={style.date_item}
                        >
                            <DatePicker
                                showTime
                                format={DATE_TIME_FORMAT}
                                placeholder="Select end date and time"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </div>
                </div>

                <div>
                    <h5 className={style.subsection_header}>Location</h5>
                    <div className={style.location_row}>
                        <Form.Item
                            name="latitude"
                            label="Latitude"
                            className={style.location_item}
                        >
                            <Input
                                type="number"
                                step="any"
                                placeholder="Enter latitude (-90 to 90)"
                                min={-90}
                                max={90}
                            />
                        </Form.Item>

                        <Form.Item
                            name="longitude"
                            label="Longitude"
                            className={style.location_item}
                        >
                            <Input
                                type="number"
                                step="any"
                                placeholder="Enter longitude (-180 to 180)"
                                min={-180}
                                max={180}
                            />
                        </Form.Item>
                    </div>
                    <div className={style.location_row}>
                        <Form.Item
                            name="provinces"
                            label="Region"
                            className={style.location_item_full_width}
                            getValueProps={(value: PromotionFormProvince[]) => {
                                // Transform PromotionFormProvince[] to number[] for TreeSelect
                                if (!value || !Array.isArray(value)) {
                                    return { value: [] };
                                }
                                // Extract province IDs for TreeSelect
                                return {
                                    value: value.map((p) => p.provinceId),
                                };
                            }}
                            getValueFromEvent={(
                                selectedProvinceIds: number[]
                            ) => {
                                // Transform from number[] (province IDs) back to PromotionFormProvince[]
                                if (
                                    !selectedProvinceIds ||
                                    !Array.isArray(selectedProvinceIds)
                                ) {
                                    return [];
                                }
                                // Get current form value to preserve existing relation IDs
                                const currentSelectedProvinces =
                                    form.getFieldValue('provinces') || [];

                                return handleProvinceSelectionChange(
                                    selectedProvinceIds,
                                    currentSelectedProvinces
                                );
                            }}
                        >
                            <BusinessUnitsSelectTree />
                        </Form.Item>
                    </div>
                </div>

                <div>
                    <h5 className={style.subsection_header}>Status</h5>
                    <Form.Item
                        name="isActive"
                        label="Active"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </div>
            </div>
        </div>
    );
}

export const EventPage = observer(() => {
    const { action, id } = useParams<{ action: string; id?: string }>();
    const eventStore = useEventStore();
    const navigation = useNavigation();
    const { redirectAfterCreate } = usePlacementLinking();
    const authStore = useAuthStore();
    const { hasManagementAccess } = authStore;
    const [form] = Form.useForm();
    const [loading, setLoading] = React.useState(false);
    const [formChanged, setFormChanged] = React.useState(false);
    const [formValues, setFormValues] = React.useState<EventDates>({});
    const clearDirtyRef = React.useRef<(navigate?: () => void) => void>(
        () => undefined
    );

    const isEdit = action === 'edit';
    const eventId = id ? parseInt(id) : undefined;

    React.useEffect(() => {
        if (hasManagementAccess === false) {
            navigation.goToHome();
        }
    }, [hasManagementAccess, navigation]);

    const loadEvent = React.useCallback(async () => {
        if (!eventId) return;

        setLoading(true);
        try {
            const event = await eventStore.getEvent(eventId);
            const enTranslation = event.translations.find(
                (t) => t.language === 'en'
            );
            const frTranslation = event.translations.find(
                (t) => t.language === 'fr'
            );

            // Dates come from api as UTC strings.
            // EventFormData.startDate and endDate are a non nullable Moment, so fallback to invalid moment.
            const convertToLocal = (utcDateString: string, timezone: string) =>
                convertUtcToLocalMoment(utcDateString, timezone) ||
                moment.invalid();

            const formData: EventFormData = {
                translations: {
                    en: {
                        title: enTranslation?.title || '',
                        subtitle: enTranslation?.subtitle || '',
                        description: enTranslation?.description || '',
                        image: enTranslation?.image || '',
                        ctaText: enTranslation?.ctaText || '',
                        mapButtonText: enTranslation?.mapButtonText || '',
                    },
                    fr: {
                        title: frTranslation?.title || '',
                        subtitle: frTranslation?.subtitle || '',
                        description: frTranslation?.description || '',
                        image: frTranslation?.image || '',
                        ctaText: frTranslation?.ctaText || '',
                        mapButtonText: frTranslation?.mapButtonText || '',
                    },
                },
                timezone: event.timezone,
                startDate: convertToLocal(event.startDate, event.timezone),
                endDate: convertToLocal(event.endDate, event.timezone),
                isActive: event.isActive,
                latitude: event.latitude || undefined,
                longitude: event.longitude || undefined,
                eventProvinces: event.eventProvinces || [],
                // Transform eventProvinces to selectedProvinces format
                provinces:
                    event.eventProvinces?.map((ep) => ({
                        businessUnitId: ep.id, // Preserve relation ID
                        provinceId: ep.province.id,
                    })) || [],
            };

            form.setFieldsValue(formData);
            setFormValues(formData);
            setTimeout(() => setFormChanged(false), 100);
        } catch (error) {
            console.error('Error loading event:', error);
            message.error('Failed to load event');
        } finally {
            setLoading(false);
        }
    }, [eventId, eventStore, form]);

    const onFinish = React.useCallback(
        async (values: EventFormData) => {
            setLoading(true);
            try {
                const eventData = {
                    timezone: values.timezone,
                    startDate: convertLocalToUtcString(
                        values.startDate,
                        values.timezone
                    ),
                    endDate: convertLocalToUtcString(
                        values.endDate,
                        values.timezone
                    ),
                    isActive: values.isActive,
                    latitude:
                        values.latitude && values.latitude !== ''
                            ? Number(values.latitude)
                            : null,
                    longitude:
                        values.longitude && values.longitude !== ''
                            ? Number(values.longitude)
                            : null,
                    translations: [
                        {
                            language: 'en' as const,
                            title: values.translations.en.title,
                            subtitle: values.translations.en.subtitle,
                            description: values.translations.en.description,
                            image: values.translations.en.image,
                            ctaText: values.translations.en.ctaText,
                            mapButtonText: values.translations.en.mapButtonText,
                        },
                        {
                            language: 'fr' as const,
                            title: values.translations.fr.title,
                            subtitle: values.translations.fr.subtitle,
                            description: values.translations.fr.description,
                            image: values.translations.fr.image,
                            ctaText: values.translations.fr.ctaText,
                            mapButtonText: values.translations.fr.mapButtonText,
                        },
                    ],
                    provinces: values.provinces,
                };

                if (isEdit && eventId) {
                    await eventStore.updateEvent(eventId, eventData);
                } else {
                    await eventStore.createEvent(eventData);
                }

                setFormChanged(false);
                clearDirtyRef.current(() =>
                    redirectAfterCreate(() => navigation.goToEvents())
                );
            } catch (error) {
                message.error(
                    `Failed to ${isEdit ? 'update' : 'create'} event`
                );
            } finally {
                setLoading(false);
            }
        },
        [eventStore, isEdit, eventId, navigation, redirectAfterCreate]
    );

    const handleDelete = React.useCallback(async () => {
        if (!eventId) return;

        setLoading(true);
        try {
            await eventStore.deleteEvent(eventId);
            navigation.goToEvents();
        } catch (error) {
            message.error('Failed to delete event');
        } finally {
            setLoading(false);
        }
    }, [eventStore, eventId, navigation]);

    const handleSaveFromModal = useCallback(async () => {
        await form.validateFields();
        await onFinish(form.getFieldsValue());
    }, [form, onFinish]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formChanged,
        onSave: handleSaveFromModal,
    });
    clearDirtyRef.current = clearDirty;

    React.useEffect(() => {
        if (isEdit && eventId && hasManagementAccess) {
            loadEvent();
        }
    }, [isEdit, eventId, loadEvent, hasManagementAccess]);

    if (hasManagementAccess === false) {
        return null;
    }

    // Calculate event status if we have dates
    const eventStatus =
        isEdit && eventId && formValues.startDate && formValues.endDate
            ? getEventStatus(
                  new Date(formValues.startDate.toDate()),
                  new Date(formValues.endDate.toDate())
              )
            : null;

    return (
        <>
            <PageTopBar
                title={isEdit ? `Event #${eventId}` : 'New Event'}
                goBack={{
                    callback: () => navigation.goToEvents(),
                    text: 'Back to list',
                }}
            >
                <>
                    {eventStatus && <EventStatusTag status={eventStatus} />}
                    {isEdit && eventId && (
                        <DeleteButton
                            onClick={handleDelete}
                            loading={loading}
                        />
                    )}
                    <SaveButton
                        htmlType="submit"
                        loading={loading}
                        disabled={isEdit && !formChanged && !loading}
                        form="event-form"
                    >
                        Save
                    </SaveButton>
                </>
            </PageTopBar>

            <div className="content_with_margins">
                <Form
                    id="event-form"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={() => {
                        setFormChanged(true);
                        setFormValues(form.getFieldsValue());
                    }}
                    initialValues={{
                        isActive: true,
                        timezone: getUserTimezoneOrFallback(),
                        translations: { en: {}, fr: {} },
                    }}
                    style={{
                        display: loading ? 'none' : 'flex',
                        flexDirection: 'column',
                        padding: '0.4rem',
                    }}
                >
                    <div>
                        {loading && <Spin />}

                        <h4 className={style.section_header}>Config</h4>
                        <EventConfigSection />

                        <h4 className={style.section_header}>Details</h4>
                        <Card className={style.details_card}>
                            <div className={style.translations_container}>
                                <TranslationForm
                                    language="en"
                                    form={form}
                                    setFormChanged={setFormChanged}
                                />
                                <TranslationForm
                                    language="fr"
                                    form={form}
                                    setFormChanged={setFormChanged}
                                />
                            </div>
                        </Card>
                    </div>
                </Form>
            </div>
        </>
    );
});
